import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Tabs, 
  Tab,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getCensusTracts, getAggregatedCensusData, getCensusMetrics, getFoodInsecurityData } from '../../utilityFunctions/DataloveUtil';
import Colors from '../../components/ui/Colors';
import FoodInsecurityMap from '../../components/maps/FoodInsecurityMap';
import LowIncomeTractsMap from '../../components/maps/LowIncomeTractsMap';
import PovertyRateMap from '../../components/maps/PovertyRateMap';
import FoodAccessMap from '../../components/maps/FoodAccessMap';

import { processCountyData } from '../../components/utils/mapUtils';
import CensusMetricChart from '../../components/CensusMetricChart';
import CustomLoader from "../../components/CustomLoader";

const STATES = [
  { code: "all", name: "All States" },
  { code: "01", name: "Alabama" },
  { code: "02", name: "Alaska" },
  { code: "04", name: "Arizona" },
  { code: "05", name: "Arkansas" },
  { code: "06", name: "California" },
  { code: "08", name: "Colorado" },
  { code: "09", name: "Connecticut" },
  { code: "10", name: "Delaware" },
  { code: "11", name: "District of Columbia" },
  { code: "12", name: "Florida" },
  { code: "13", name: "Georgia" },
  { code: "15", name: "Hawaii" },
  { code: "16", name: "Idaho" },
  { code: "17", name: "Illinois" },
  { code: "18", name: "Indiana" },
  { code: "19", name: "Iowa" },
  { code: "20", name: "Kansas" },
  { code: "21", name: "Kentucky" },
  { code: "22", name: "Louisiana" },
  { code: "23", name: "Maine" },
  { code: "24", name: "Maryland" },
  { code: "25", name: "Massachusetts" },
  { code: "26", name: "Michigan" },
  { code: "27", name: "Minnesota" },
  { code: "28", name: "Mississippi" },
  { code: "29", name: "Missouri" },
  { code: "30", name: "Montana" },
  { code: "31", name: "Nebraska" },
  { code: "32", name: "Nevada" },
  { code: "33", name: "New Hampshire" },
  { code: "34", name: "New Jersey" },
  { code: "35", name: "New Mexico" },
  { code: "36", name: "New York" },
  { code: "37", name: "North Carolina" },
  { code: "38", name: "North Dakota" },
  { code: "39", name: "Ohio" },
  { code: "40", name: "Oklahoma" },
  { code: "41", name: "Oregon" },
  { code: "42", name: "Pennsylvania" },
  { code: "44", name: "Rhode Island" },
  { code: "45", name: "South Carolina" },
  { code: "46", name: "South Dakota" },
  { code: "47", name: "Tennessee" },
  { code: "48", name: "Texas" },
  { code: "49", name: "Utah" },
  { code: "50", name: "Vermont" },
  { code: "51", name: "Virginia" },
  { code: "53", name: "Washington" },
  { code: "54", name: "West Virginia" },
  { code: "55", name: "Wisconsin" },
  { code: "56", name: "Wyoming" }
];

// Define the metrics we want to display
const METRICS = [
  { value: 'foodInsecurityRate', label: 'Food Insecurity Rate', color: '#E31A1C' },
  { value: 'lowIncomeTracts', label: 'Low Income Areas', color: '#6A51A3' },
  { value: 'povertyRate', label: 'Poverty Rate', color: '#2171B5' },
  { value: 'foodAccessIndex', label: 'Food Access Rate', color: '#238B45' }
];


const CensusTractsVisualization = (props) => {
  // Get loginUser from props
  const { loginUser } = props;
  const token = loginUser?.jwtToken;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Add a mounted ref to track component mount state
  const isMounted = useRef(true);
  
  // Data states
  const [censusTracts, setCensusTracts] = useState([]);
  const [foodInsecurityData, setFoodInsecurityData] = useState([]);
  
  // Filter states
  const [selectedState, setSelectedState] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('foodInsecurityRate');
  
  // County list for the selected state
  const [counties, setCounties] = useState([]);
  
  // Add a state to prevent multiple renders
  const [renderKey, setRenderKey] = useState(0);
  
  // Set up cleanup function
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);
  
  // Modified data processing
  const processedData = useMemo(() => 
    processCountyData(foodInsecurityData), 
    [foodInsecurityData]
  );
  
  // Updated load function
  const loadFoodInsecurityData = useCallback(async () => {
    if (!token) {
      setError("Authentication token is missing");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("[Data] Fetching food insecurity data for state:", selectedState || "All States");
      
      const stateFilter = selectedState && selectedState !== "all" ? selectedState : "";
      const filters = { state: stateFilter };
      
      const data = await getFoodInsecurityData(filters, token);
      
      if (!isMounted.current) return;
      
      const dataArray = Array.isArray(data) ? data : [];
      
      if (dataArray.length === 0) {
        console.warn("[Data] API returned empty dataset");
        setFoodInsecurityData([]);
        setIsLoading(false);
        return;
      }

      console.log("[Data] Raw data:", dataArray);
      
      const processed = processCountyData(
        dataArray
          .filter(item => item !== null)
          .map(item => ({
            // Preserve existing fields
            ...item,
            
            // Map fields to correct names
            lowIncomeTracts: item.highRiskFlag === 1 || item.highRiskFlag === '1' ? 1 : 0,
            povertyRate: parseFloat(item.povertyImpactFactor) || 0,
            foodAccessIndex: parseFloat(item.lowAccessRate) || 0, // New field
            
            // Renamed for clarity
            povertyImpactFactor: undefined, // Remove old field
            lowAccessRate: undefined,       // Remove old field
            
            // Standardized fields
            stateCode: item.stateCode || '',
            countyName: item.countyName || '',
            foodInsecurityRate: parseFloat(item.foodInsecurityRate) || 0,
            population: parseInt(item.population) || 0,
            
            // Optional: Add field validation
            fips: item.fips ? String(item.fips).padStart(5, '0') : ''
          }))
      );

      console.log("[DATA] Processed structure:", {
        count: processed.length,
        sample: processed.slice(0, 3),
        lowIncomeCount: processed.filter(d => d.lowIncomeTracts === 1).length
      });

      setFoodInsecurityData(processed);
      
      setRenderKey(prev => prev + 1);

    } catch (err) {
      console.error("[Error] Data load failed:", err.message);
      if (isMounted.current) {
        setError("Failed to load data: " + err.message);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [selectedState, token]);
  
  // Load data when component mounts or filters change
  useEffect(() => {
    loadFoodInsecurityData();
  }, [loadFoodInsecurityData]);
  
  // Updated map rendering
  const renderMap = () => {
    if (isLoading) {
      return <CustomLoader />;
    }
    
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    
    if (!foodInsecurityData || foodInsecurityData.length === 0) {
      return (
        <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Data Available
          </Typography>
          <Typography variant="body1">
            No food insecurity data is available. Please try different filter options.
          </Typography>
        </Paper>
      );
    }
    
    // Run this check only once after loading data
    if (!window.dataChecked && foodInsecurityData.length > 0) {
      window.dataChecked = true;
      
      // DEBUG: Check what data we have for low income tracts
      const lowIncomeCount = foodInsecurityData.filter(item => item.lowIncomeTracts > 0).length;
      console.log(`Found ${lowIncomeCount} counties with lowIncomeTracts > 0 out of ${foodInsecurityData.length} total`);
      
      if (lowIncomeCount === 0) {
        console.warn("⚠️ WARNING: No counties have lowIncomeTracts > 0, visualization may appear blank!");
      } else {
        console.log("Counties with lowIncomeTracts=1:", 
          foodInsecurityData
            .filter(item => item.lowIncomeTracts > 0)
            .map(item => `${item.countyName}, ${item.stateCode}`)
            .join("; ")
        );
      }
    }
    
    const metricInfo = METRICS.find(m => m.value === selectedMetric) || METRICS[0];
    
    // Function to handle exporting/printing the current view
    const handleExport = () => {
      const printContent = document.getElementById('census-map-container');
      const printWindow = window.open('', '_blank');
      
      // Get metric name and state selection for the title
      const metricName = metricInfo.label;
      const stateName = selectedState ? 
        STATES.find(s => s.code === selectedState)?.name || 'Selected State' : 
        'United States';
      
      printWindow.document.write(`
        <html>
          <head>
            <title>${metricName} Map - ${stateName}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { color: #333; font-size: 24px; margin-bottom: 10px; }
              .date { color: #666; margin-bottom: 20px; }
              .map-container { margin: 20px 0; }
            </style>
          </head>
          <body>
            <h1>${metricName} by County - ${stateName}</h1>
            <div class="date">Generated on ${new Date().toLocaleDateString()}</div>
            <div class="map-container">
              ${printContent.innerHTML}
            </div>
            <p><strong>About ${metricName}:</strong> ${getMetricDescription(selectedMetric)}</p>
            <p><small>Source: Census Tract Data Analysis</small></p>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      
      // Add slight delay before printing
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
    
    // Handler for changing metric that won't cause infinite rerenders
    const handleMetricChange = (newMetric) => {
      if (newMetric !== selectedMetric) {
        console.log(`Setting selectedMetric to: ${newMetric}`);
        setSelectedMetric(newMetric);
        // Increment render key to force a clean rerender
        setRenderKey(prev => prev + 1);
      }
    };
    
    return (
      <>
        {/* Metric Selection Bar with "Chips" for each metric */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
            Select Metric to Visualize
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {METRICS.map(metric => (
              <Button 
                key={metric.value}
                variant={selectedMetric === metric.value ? "contained" : "outlined"}
                color="primary"
                size="medium"
                onClick={() => handleMetricChange(metric.value)}
                sx={{ 
                  minWidth: '180px', 
                  borderRadius: '24px',
                  backgroundColor: selectedMetric === metric.value ? Colors.secondary : 'transparent',
                  '&:hover': {backgroundColor: selectedMetric === metric.value ? Colors.hover : 'rgba(0, 0, 0, 0.04)'},
                  fontWeight: selectedMetric === metric.value ? 'bold' : 'normal'
                }}
              >
                {metric.label}
              </Button>
            ))}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button 
              variant="outlined" 
              size="small"
              sx={{
                backgroundColor: 'transparent',
                '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'}
              }}
              onClick={handleExport}
              startIcon={<span role="img" aria-label="export">📊</span>}
            >
              Export Map
            </Button>
          </Box>
        </Paper>
        
        <Paper sx={{ p: 3, height: 'calc(100% - 80px)' }} id="census-map-container">
          <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
            {selectedMetric === 'lowIncomeTracts' ? (
              <LowIncomeTractsMap
                key={`lowincome-${renderKey}`}
                data={processedData}
              />
            ) : selectedMetric === 'povertyRate' ? (
              <PovertyRateMap
                key={`poverty-${renderKey}`}
                data={processedData}
              />
            ) : selectedMetric === 'foodAccessIndex' ? (
              <FoodAccessMap
                key={`foodaccess-${renderKey}`}
                data={processedData}
              />
            ) : (
              <FoodInsecurityMap
                key={`foodinsecurity-${renderKey}`}
                data={processedData}
              />
            )}
          </Box>
        </Paper>
        
        {/* Metric Description Card */}
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            About {metricInfo.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getMetricDescription(selectedMetric)}
          </Typography>
        </Paper>
      </>
    );
  };
  
  // Helper function to get metric descriptions
  const getMetricDescription = (metric) => {
    switch(metric) {
      case 'foodInsecurityRate':
        return 'The percentage of the population that lacks adequate access to sufficient food due to limited resources. Higher values indicate areas with greater food insecurity challenges.';
      case 'lowIncomeTracts':
        return 'Areas identified as low-income census tracts where a significant percentage of residents are economically disadvantaged. These tracts often face greater challenges accessing healthy food.';
      case 'povertyRate':
        return 'The percentage of people living below the federal poverty threshold. This metric helps identify economically vulnerable populations.';
      case 'foodAccessIndex':
        return 'The percentage of the population with limited access to grocery stores or supermarkets. Higher values indicate areas where residents have greater difficulty accessing fresh, healthy food.';
      default:
        return 'Select a metric to view its description.';
    }
  };
  
  // Render the filters
  const renderFilters = () => {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="state-select-label">State</InputLabel>
              <Select
                labelId="state-select-label"
                id="state-select"
                value={selectedState}
                label="State"
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <MenuItem value="">All States</MenuItem>
                {STATES.map(state => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="metric-select-label">Metric</InputLabel>
              <Select
                labelId="metric-select-label"
                id="metric-select"
                value={selectedMetric}
                label="Metric"
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                {METRICS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4} md={3}>
            <Button 
              variant="outlined" 
              color="primary" 
              size="small"
              fullWidth
              sx={{
                backgroundColor: Colors.secondary,
                color: 'white',
                '&:hover': {backgroundColor: Colors.hover},
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
              onClick={() => {
                setSelectedState("");
              }}
              disabled={!selectedState}
            >
              Reset to Full US View
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  };
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {renderFilters()}
      
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {renderMap()}
      </Box>
    </Box>
  );
};

export default CensusTractsVisualization; 